import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import './Header.css';
import { useAuth } from '../../contexts/AuthContext';
import LoginModal from '../../modals/LoginModal/LoginModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Home, Storefront, InfoOutlined } from '@mui/icons-material';
import { faMessage, faUser, faBell } from '@fortawesome/free-regular-svg-icons';
import { faBlog } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'motion/react';
import { FiMapPin, FiChevronDown } from 'react-icons/fi';
import LocationPickerModal from '../../modals/LocationPicker/LocationPickerModal';
import { useLocation as useLocationContext } from '../../contexts/LocationContext';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

const formatHeaderLocation = (location) => {
  if (!location) return 'Select location';

  const area = location.village || location.neighbourhood || location.street;
  const city = location.city;

  if (area && city && area !== city) {
    return `${area}, ${city}`;
  }

  if (location.display) {
    const parts = location.display
      .split(',')
      .map(part => part.trim())
      .filter(Boolean);

    if (parts.length >= 2) {
      return `${parts[0]}, ${parts[1]}`;
    }
  }

  return city || area || location.display || 'Select location';
};

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const menuRef = useRef(null);

  const routerLocation = useRouterLocation();
  const { location: userLocation } = useLocationContext();
  const isHome = routerLocation.pathname === "/";
  const isChatScreen = routerLocation.pathname === "/chat";

  const { currentUser, logout } = useAuth();

  const navigate = useNavigate();
  const [showChatPopup, setShowChatPopup] = useState(false);

  const [totalUnread, setTotalUnread] = useState(0);

  const handleLogout = async () => {
    try {
      await logout();
      if (isChatScreen || routerLocation.pathname.startsWith('/profile')
        || routerLocation.pathname.startsWith('/explore') || routerLocation.pathname.startsWith('/edit-ad')
        || routerLocation.pathname.startsWith('/item') || routerLocation.pathname.startsWith('/postad')
        || routerLocation.pathname.startsWith('/my-ads')) {
        navigate('/');
      }
      console.log("Logged out successfully");
    } catch (error) {
      console.error("Failed to log out", error);
    }
  };

  useEffect(() => {
    const handler = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false); // only close if clicked outside
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);


  const handlePostAdClick = () => {
    if (!currentUser) {
      setIsLoginModalOpen(true);
    } else {
      navigate("/postad");
    }
  };

  const handleChatClick = () => {
    if (routerLocation.pathname.startsWith('/chat')) {
      setShowChatPopup(true);
    } else {
      navigate('/chat');
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  //NEW: Listen to all chats to calculate the total unread badge

  useEffect(() => {
    if (!currentUser) {
      setTotalUnread(0);
      return;
    }

    const q = query(
      collection(db, 'chat_rooms'),
      where('participantIds', 'array-contains', currentUser.uid),
      where('hasMessages', '==', true)
    );

    const unsubscribe = onSnapshot(q, (shapshot) => {
      let unreadSum = 0;
      shapshot.docs.forEach(doc => {
        const data = doc.data();
        // Add up the unread counts specifically for this user
        const myUnread = data.unread_counts?.[currentUser.uid] || 0;
        unreadSum += myUnread;
      });
      setTotalUnread(unreadSum);
    });

    return () => unsubscribe();
  }, [currentUser])

  return (
    <div className={`header 
  ${scrolled ? "scrolled" : ""} 
  ${isHome && !scrolled ? "transparent" : "solid"}`}>

      <header className="header-inner">
        <div className="header-container">

          <div className="logo">
            <img
              src="/logos/auth_version_cream_400x200.png"
              alt="Qinto Logo"
              className="logo-image"
            />
          </div>

          {/* --- LOCATION SELECTOR --- */}
          <div className="location-selector" onClick={() => setIsPickerOpen(true)}>
            <FiMapPin className="pin-icon" />
            <span className="loc-text">{formatHeaderLocation(userLocation)}</span>
            <FiChevronDown />
          </div>

          <button
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            ☰
          </button>

          <nav className={`nav ${mobileMenuOpen ? "mobile-open" : ""}`}>

            <NavLink to="/" className={({ isActive }) =>
              isActive ? "nav-link active" : "nav-link"
            } onClick={() => setMobileMenuOpen(false)}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', height: '100%' }}>
                <Home fontSize="small" style={{ position: 'relative', top: '-1px' }} />
                <span style={{ lineHeight: 1 }}>Home</span>
              </div>
            </NavLink>

            <NavLink to="/explore" className={({ isActive }) =>
              isActive ? "nav-link active" : "nav-link"
            } onClick={() => setMobileMenuOpen(false)}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', height: '100%' }}>
                <Storefront fontSize="small" style={{ position: 'relative', top: '-1px' }} />
                <span style={{ lineHeight: 1 }}>Marketplace</span>
              </div>
            </NavLink>

            <NavLink to="/about" className={({ isActive }) =>
              isActive ? "nav-link active" : "nav-link"
            } onClick={() => setMobileMenuOpen(false)}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', height: '100%' }}>
                <InfoOutlined fontSize="small" style={{ position: 'relative', top: '-1px' }} />
                <span style={{ lineHeight: 1 }}>About Us</span>
              </div>
            </NavLink>

            <NavLink to="/blog" className={({ isActive }) =>
              isActive ? "nav-link active" : "nav-link"
            } onClick={() => setMobileMenuOpen(false)}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', height: '100%' }}>
                <FontAwesomeIcon icon={faBlog} style={{ fontSize: '16px', position: 'relative', top: '-1px' }} />
                <span style={{ lineHeight: 1 }}>Blog</span>
              </div>
            </NavLink>

            <div className="nav-actions">
              {currentUser ? (
                <>
                  <div className="chat-wrapper">
                    <button
                      className="nav-chat-btn"
                      onClick={() => handleChatClick()}
                      aria-label="Messages"
                      title="Messages"
                    >
                      <FontAwesomeIcon icon={faMessage} size="lg" />
                      {totalUnread > 0 && (
                        <span className="unread-badge">{totalUnread > 99 ? '99+' : totalUnread}</span>
                      )}

                    </button>
                  </div>

                  <div className="chat-wrapper">
                    <button
                      className="nav-chat-btn"
                      onClick={() => navigate('/notifications')}
                      aria-label="Notifications"
                      title="Notifications"
                    >
                      <FontAwesomeIcon icon={faBell} size="lg" />
                    </button>
                  </div>

                  {/* // IF LOGGED IN: Show Profile/Logout */}

                  <div className="profile-wrap" ref={menuRef}>
                    <button
                      type="button"
                      className="profile-trigger"
                      onClick={() => setShowMenu((prev) => !prev)}
                    >
                      {/* Wait for 2 seconds and then set the profile image */}
                      {currentUser.photoURL && <img
                        src={currentUser.photoURL || 'https://via.placeholder.com/40'}
                        alt="Profile"
                        style={{ width: '35px', height: '35px', borderRadius: '50%' }}
                      />}
                      {!currentUser.photoURL && <FontAwesomeIcon icon={faUser} style={{ width: '25px', height: '23px', padding: '5px', color: '#ccc', border: '2px solid #ccc', borderRadius: '50%' }} />}
                    </button>

                    <AnimatePresence>
                      {showMenu && (
                        <motion.div
                          initial={{ opacity: 0, y: -8, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -8, scale: 0.98 }}
                          className="profile-dropdown"
                        >
                          <div className="profile-dropdown-arrow" />
                          <div className="profile-header">
                            {currentUser.photoURL && <img src={currentUser.photoURL} alt="user" />}
                            {!currentUser.photoURL && <FontAwesomeIcon icon={faUser} size="2x" />}
                            <div>
                              <h4>{currentUser.displayName || currentUser.name}</h4>
                              <button
                                className="profile-btn"
                                onClick={() => {
                                  setShowMenu(false);
                                  navigate("/profile");
                                }}
                              >
                                View Profile
                              </button>
                            </div>
                          </div>

                          <div
                            className="menu-item"
                            onClick={() => {
                              setShowMenu(false);
                              navigate("/my-ads");
                            }}
                          >
                            My Ads
                          </div>

                          <div
                            className="menu-item"
                            onClick={() => {
                              setShowMenu(false);
                              navigate("/playstore/?qintoapp");
                            }}
                          >
                            Install Qinto App
                          </div>

                          <div className="menu-divider" />

                          <div
                            className="menu-item logout"
                            onClick={async () => {
                              setShowMenu(false);
                              await handleLogout();
                            }}
                          >
                            Logout
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </>
              ) : (
                // IF NOT LOGGED IN: Show Sign In button
                <button
                  onClick={() => setIsLoginModalOpen(true)}
                  className="btn btn-primary-glossy"
                  style={{ padding: '8px 16px' }}
                >
                  Sign In
                </button>
              )}

              <button className="qinto-sell-btn" onClick={() => handlePostAdClick()}>
                <span className="sell-icon">+</span>
                <span>Post Ad</span>
              </button>
            </div>



          </nav>


        </div>

        {isPickerOpen && (
          <LocationPickerModal onClose={() => setIsPickerOpen(false)} />
        )}

        {showChatPopup && (
          <div
            className="chat-popup-overlay"
            onClick={() => setShowChatPopup(false)}
          >
            <div
              className="chat-popup-box"
              onClick={(e) => e.stopPropagation()}
            >
              <p>You are already on the chat screen.</p>
              <button onClick={() => setShowChatPopup(false)}>OK</button>
            </div>
          </div>
        )}

        <LoginModal
          isOpen={isLoginModalOpen}
          onClose={() => setIsLoginModalOpen(false)}
        />
      </header>
    </div>

  );
};
